import { createTheme, PaletteColor, PaletteColorOptions } from '@mui/material';

import '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    forest: PaletteColor;
    sand: PaletteColor;
    taiga: PaletteColor;
    ocean: PaletteColor;
    desert: PaletteColor;
    ginko: PaletteColor;
    sky: PaletteColor;
    forestShade: PaletteColor;
    forestTint1: PaletteColor;
    forestTint2: PaletteColor;
    sandShade4: PaletteColor;
    sandShade3: PaletteColor;
    sandShade2: PaletteColor;
    sandShade1: PaletteColor;
    sandTint: PaletteColor;
    taigaTint1: PaletteColor;
    taigaTint2: PaletteColor;
    oceanShade: PaletteColor;
    oceanTint: PaletteColor;
    desertTint: PaletteColor;
    autoFill: PaletteColor;
    errorBackground: PaletteColor;
    errorBorder: PaletteColor;
    errorText: PaletteColor;
    warningBackground: PaletteColor;
    warningBorder: PaletteColor;
    warningText: PaletteColor;
    successBackground: PaletteColor;
    successBorder: PaletteColor;
    successText: PaletteColor;
    informationBackground: PaletteColor;
    informationBorder: PaletteColor;
    informationText: PaletteColor;
  }

  interface PaletteOptions {
    forest: PaletteColorOptions;
    sand: PaletteColorOptions;
    taiga: PaletteColorOptions;
    ocean: PaletteColorOptions;
    desert: PaletteColorOptions;
    ginko: PaletteColorOptions;
    sky: PaletteColorOptions;
    forestShade: PaletteColorOptions;
    forestTint1: PaletteColorOptions;
    forestTint2: PaletteColorOptions;
    sandShade4: PaletteColorOptions;
    sandShade3: PaletteColorOptions;
    sandShade2: PaletteColorOptions;
    sandShade1: PaletteColorOptions;
    sandTint: PaletteColorOptions;
    taigaTint1: PaletteColorOptions;
    taigaTint2: PaletteColorOptions;
    oceanShade: PaletteColorOptions;
    oceanTint: PaletteColorOptions;
    desertTint: PaletteColorOptions;
    autoFill: PaletteColorOptions;
    errorBackground: PaletteColorOptions;
    errorBorder: PaletteColorOptions;
    errorText: PaletteColorOptions;
    warningBackground: PaletteColorOptions;
    warningBorder: PaletteColorOptions;
    warningText: PaletteColorOptions;
    successBackground: PaletteColorOptions;
    successBorder: PaletteColorOptions;
    successText: PaletteColorOptions;
    informationBackground: PaletteColorOptions;
    informationBorder: PaletteColorOptions;
    informationText: PaletteColorOptions;
  }
}

const brandColors = {
  primary: {
    forest: '#124734',
    sand: '#FAF2E4',
    taiga: '#373A36',
  },
  secondary: {
    ocean: '#004D71',
    desert: '#FFA400',
    ginko: '#A2D45E',
    sky: '#B9DBE5',
  },
  ui: {
    forestShade: '#0C2E22',
    forestTint1: '#196147',
    forestTint2: '#7C9288',
    sandShade4: '#C2BBB0',
    sandShade3: '#D1CABF',
    sandShade2: '#E0D9CC',
    sandShade1: '#F0E8DA',
    sandTint: '#FAF7F2',
    taigaTint1: '#5C615A',
    taigaTint2: '#80877E',
    oceanShade: '#003B57',
    oceanTint: '#005E8A',
    desertTint: '#FFB633',
  },
  semantic: {
    error: '#AD0322',
    errorBackground: '#F2DAD0',
    errorBorder: '#E3AAA9',
    errorText: '#570211',
    warning: '#FAA614',
    warningBackground: '#FAE3BA',
    warningBorder: '#FACC7C',
    warningText: '#3F2A05',
    success: '#008738',
    successBackground: '#E1E7D2',
    successBorder: '#AFD2B0',
    successText: '#00220E',
    information: '#006BB2',
    informationBackground: '#D5DEDD',
    informationBorder: '#AFC9D5',
    informationText: '#003659',
    autofill: '#E3EEF5',
  },
} as const;

const base = createTheme({});

const { augmentColor } = base.palette;
const makeColor = (main: string) => augmentColor({ color: { main } });

const palette = {
  mode: 'light',
  primary: makeColor(brandColors.primary.forest),
  secondary: makeColor(brandColors.secondary.ocean),
  error: makeColor(brandColors.semantic.error),
  warning: makeColor(brandColors.semantic.warning),
  info: makeColor(brandColors.semantic.information),
  success: makeColor(brandColors.semantic.success),
  background: {
    paper: brandColors.ui.sandTint,
    default: brandColors.primary.sand,
  },
  text: {
    primary: brandColors.primary.taiga,
    secondary: brandColors.ui.taigaTint1,
    disabled: brandColors.ui.taigaTint2,
  },
  divider: brandColors.ui.sandShade2,

  forest: makeColor(brandColors.primary.forest),
  sand: makeColor(brandColors.primary.sand),
  taiga: makeColor(brandColors.primary.taiga),
  ocean: makeColor(brandColors.secondary.ocean),
  desert: makeColor(brandColors.secondary.desert),
  ginko: makeColor(brandColors.secondary.ginko),
  sky: makeColor(brandColors.secondary.sky),
  forestShade: makeColor(brandColors.ui.forestShade),
  forestTint1: makeColor(brandColors.ui.forestTint1),
  forestTint2: makeColor(brandColors.ui.forestTint2),
  sandShade4: makeColor(brandColors.ui.sandShade4),
  sandShade3: makeColor(brandColors.ui.sandShade3),
  sandShade2: makeColor(brandColors.ui.sandShade2),
  sandShade1: makeColor(brandColors.ui.sandShade1),
  sandTint: makeColor(brandColors.ui.sandTint),
  taigaTint1: makeColor(brandColors.ui.taigaTint1),
  taigaTint2: makeColor(brandColors.ui.taigaTint2),
  oceanShade: makeColor(brandColors.ui.oceanShade),
  oceanTint: makeColor(brandColors.ui.oceanTint),
  desertTint: makeColor(brandColors.ui.desertTint),
  autoFill: makeColor(brandColors.semantic.autofill),
  errorBackground: makeColor(brandColors.semantic.errorBackground),
  errorBorder: makeColor(brandColors.semantic.errorBorder),
  errorText: makeColor(brandColors.semantic.errorText),
  warningBackground: makeColor(brandColors.semantic.warningBackground),
  warningBorder: makeColor(brandColors.semantic.warningBorder),
  warningText: makeColor(brandColors.semantic.warningText),
  successBackground: makeColor(brandColors.semantic.successBackground),
  successBorder: makeColor(brandColors.semantic.successBorder),
  successText: makeColor(brandColors.semantic.successText),
  informationBackground: makeColor(brandColors.semantic.informationBackground),
  informationBorder: makeColor(brandColors.semantic.informationBorder),
  informationText: makeColor(brandColors.semantic.informationText),
} as const;

export default palette;
